/**
 * Grade de orçamento — a tela que o produto é.
 *
 * O ponto arquitetural desta tela: a cubagem roda AQUI, no navegador, pelo
 * mesmo `@madeira/dominio` que o servidor usa. O volume aparece enquanto se
 * digita, sem ida ao servidor, e quando o documento é gravado o servidor
 * recalcula e chega no mesmo número — porque é o mesmo código, com o mesmo
 * arredondamento (spec §7).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  calcularLinha,
  camposDaRegra,
  Dinheiro,
  oQueMede,
  rotuloDaRegra,
  Volume,
  type CampoMedida,
  type RegraCubagem,
} from "@madeira/dominio";
import { api, type BitolaSaldo, type Filial, type Parceiro, type Produto } from "../api.ts";

interface Linha {
  chave: number;
  produtoId: string;
  produtoRotulo: string;
  /** Valores crus por chave de campo, como o usuário digitou. */
  medida: Record<string, string>;
  preco: string;
}

interface ItemSemente {
  espMm: number | null; largMm: number | null; compM: string | null;
  pecas: number; precoUnitario: string;
}

interface Props {
  filiais: Filial[];
  parceiros: Parceiro[];
  produtos: Produto[];
  aviso: (texto: string, desfazer?: () => void) => void;
  /** Itens copiados de um orçamento existente — o "duplicar" da listagem. */
  semente?: { itens: ItemSemente[]; parceiroId: string } | null;
  aoSalvar?: () => void;
}

let proximaChave = 1;

function linhaVazia(): Linha {
  return { chave: proximaChave++, produtoId: "", produtoRotulo: "", medida: {}, preco: "" };
}

export function Orcamento({ filiais, parceiros, produtos, aviso, semente, aoSalvar }: Props) {
  const clientes = useMemo(
    () => parceiros.filter((p) => p.papel === "CLIENTE" || p.papel === "AMBOS"),
    [parceiros],
  );

  const [filialId, setFilialId] = useState(() => filiais[0]?.id ?? "");
  const [parceiroId, setParceiroId] = useState(
    () => semente?.parceiroId ?? clientes[0]?.id ?? "",
  );
  const [linhas, setLinhas] = useState<Linha[]>(() =>
    semente
      ? semente.itens.map((i) => ({
          chave: proximaChave++,
          produtoId: "",
          produtoRotulo: "",
          medida: {
            esp_mm: i.espMm === null ? "" : String(i.espMm),
            larg_mm: i.largMm === null ? "" : String(i.largMm),
            comp_m: i.compM ?? "",
            pecas: String(i.pecas),
          },
          preco: Number(i.precoUnitario).toFixed(2),
        }))
      : [linhaVazia()],
  );
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [buscaLinha, setBuscaLinha] = useState(0);
  const [confirmar, setConfirmar] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [saldos, setSaldos] = useState<Record<string, BitolaSaldo[]>>({});
  const desfazerRef = useRef<{ indice: number; linha: Linha } | null>(null);
  const gradeRef = useRef<HTMLDivElement>(null);

  const parceiro = clientes.find((p) => p.id === parceiroId);

  /**
   * A regra vem do PARCEIRO e é só exibida (§6.1). Não existe seletor de
   * fórmula nesta tela — trocar a regra é editar o cadastro do parceiro.
   */
  const regra = (parceiro?.regraCubagem ?? "SERRADA") as RegraCubagem;
  const campos = useMemo(() => camposDaRegra(regra), [regra]);

  /* Saldo por produto, para a coluna de disponibilidade. */
  useEffect(() => {
    const ids = [...new Set(linhas.map((l) => l.produtoId).filter(Boolean))];
    for (const id of ids) {
      if (saldos[id]) continue;
      void api
        .get<{ bitolas: BitolaSaldo[] }>(`/estoque/saldo/${id}`)
        .then((r) => setSaldos((s) => ({ ...s, [id]: r.bitolas })))
        .catch(() => {});
    }
  }, [linhas, saldos]);

  /**
   * Cálculo por linha, no cliente.
   *
   * Tolerante: linha incompleta é estado normal de digitação e não vira erro
   * na tela — quem recusa o documento é o F9 (§5.2).
   */
  const calculadas = useMemo(
    () =>
      linhas.map((l) => {
        const coef = parceiro?.coeficienteEmpilhamento;
        const medida =
          regra === "EMPILHAMENTO" && coef ? { coeficiente: coef, ...l.medida } : l.medida;
        const r = calcularLinha(regra, { medida, precoUnitario: l.preco || "0" });
        const semProduto = l.produtoId === "";
        return { linha: l, resultado: r, semProduto };
      }),
    [linhas, regra, parceiro],
  );

  const totais = useMemo(() => {
    let volume = Volume.ZERO;
    let valor = Dinheiro.ZERO;
    let pecas = 0;
    let pendencias = 0;
    for (const c of calculadas) {
      if (!c.resultado.ok || c.semProduto) {
        // Linha nova e ainda vazia não conta como pendência: seria hostil
        // acusar erro no instante em que o operador cria a linha.
        if (temAlgumDado(c.linha)) pendencias++;
        continue;
      }
      volume = volume.mais(c.resultado.volume);
      valor = valor.mais(c.resultado.total);
      pecas += Number(c.linha.medida["pecas"] ?? 0);
    }
    return { volume, valor, pecas, pendencias };
  }, [calculadas]);

  const podeConverter = totais.pendencias === 0 && totais.volume.ehPositivo();

  /* --------------------------------------------------------- edição */

  const mudarCampo = useCallback((i: number, chave: string, valor: string) => {
    setLinhas((ls) =>
      ls.map((l, k) => (k === i ? { ...l, medida: { ...l.medida, [chave]: valor } } : l)),
    );
  }, []);

  const mudarPreco = useCallback((i: number, valor: string) => {
    setLinhas((ls) => ls.map((l, k) => (k === i ? { ...l, preco: valor } : l)));
  }, []);

  const novaLinha = useCallback(() => {
    setLinhas((ls) => [...ls, linhaVazia()]);
  }, []);

  /**
   * Remove sem perguntar, com desfazer no rodapé por 8s (§5.2).
   * Confirmação em ação frequente vira reflexo de confirmar sem ler.
   */
  const removerLinha = useCallback(
    (i: number) => {
      setLinhas((ls) => {
        if (ls.length <= 1) return ls;
        const removida = ls[i]!;
        desfazerRef.current = { indice: i, linha: removida };
        aviso(`Linha ${i + 1} removida ·`, () => {
          const g = desfazerRef.current;
          if (!g) return;
          desfazerRef.current = null;
          setLinhas((atual) => {
            const copia = [...atual];
            copia.splice(g.indice, 0, g.linha);
            return copia;
          });
        });
        return ls.filter((_, k) => k !== i);
      });
    },
    [aviso],
  );

  const aplicarProduto = useCallback((i: number, p: Produto) => {
    setLinhas((ls) =>
      ls.map((l, k) =>
        k === i
          ? { ...l, produtoId: p.id, produtoRotulo: `${p.especie} · ${p.forma.toLowerCase()}${p.classe ? ` · ${p.classe}` : ""}` }
          : l,
      ),
    );
  }, []);

  /* --------------------------------------------------------- gravar */

  const gravar = useCallback(
    async (converter: boolean) => {
      if (totais.pendencias > 0) {
        aviso(`Não salvo · ${totais.pendencias} pendência(s) na grade`);
        return;
      }
      const itens = calculadas
        .filter((c) => c.resultado.ok && !c.semProduto)
        .map((c) => ({
          produtoId: c.linha.produtoId,
          medida: c.linha.medida,
          precoUnitario: c.linha.preco,
        }));
      if (itens.length === 0) {
        aviso("Nada a salvar — a grade está vazia");
        return;
      }

      setGravando(true);
      try {
        const orc = await api.post<{ id: string; numero: number; volumeTotal: string }>(
          "/orcamentos",
          { filialId, parceiroId, itens },
        );
        // Confere o cálculo do cliente contra o do servidor. Se divergissem, o
        // pacote compartilhado teria falhado no seu único propósito.
        if (orc.volumeTotal !== totais.volume.paraBanco()) {
          aviso(
            `ATENÇÃO: servidor calculou ${orc.volumeTotal} e a tela ${totais.volume.paraBanco()}`,
          );
        }
        if (converter) {
          const ped = await api.post<{ numero: number }>(`/orcamentos/${orc.id}/converter`);
          aviso(`Orçamento ${orc.numero} convertido no pedido ${ped.numero}`);
        } else {
          aviso(`Orçamento ${orc.numero} salvo · ${totais.volume.paraExibicao()} m³`);
        }
        setLinhas([linhaVazia()]);
        setSaldos({});
        aoSalvar?.();
      } catch (e) {
        aviso(e instanceof Error ? e.message : "falha ao salvar");
      } finally {
        setGravando(false);
        setConfirmar(false);
      }
    },
    [calculadas, filialId, parceiroId, totais, aviso, aoSalvar],
  );

  /* -------------------------------------------------------- teclado */

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (buscaAberta || confirmar) return;

      if (e.key === "F2") {
        e.preventDefault();
        setBuscaLinha(linhaEmFoco(gradeRef.current));
        setBuscaAberta(true);
        return;
      }
      if (e.key === "F4") { e.preventDefault(); novaLinha(); return; }
      if (e.key === "F9") { e.preventDefault(); void gravar(false); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (podeConverter) setConfirmar(true);
        else aviso(`Converter bloqueado · ${totais.pendencias} pendência(s)`);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "Delete" || e.key === "Backspace")) {
        const i = linhaEmFoco(gradeRef.current);
        if (i >= 0) { e.preventDefault(); removerLinha(i); }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        const g = desfazerRef.current;
        if (!g) { aviso("Nada para desfazer"); return; }
        desfazerRef.current = null;
        setLinhas((atual) => {
          const copia = [...atual];
          copia.splice(g.indice, 0, g.linha);
          return copia;
        });
        aviso("Linha restaurada");
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const alvo = vizinhoVertical(e.key === "ArrowDown" ? 1 : -1);
        if (alvo) { e.preventDefault(); alvo.focus(); alvo.select(); }
      }
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [buscaAberta, confirmar, novaLinha, gravar, removerLinha, podeConverter, totais, aviso]);

  /** Enter anda para o próximo campo; no último da última linha, cria linha. */
  function aoPressionarNaCelula(e: React.KeyboardEvent<HTMLInputElement>, ultima: boolean) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const campos = [...(gradeRef.current?.querySelectorAll<HTMLInputElement>("input[data-celula]") ?? [])];
    const i = campos.indexOf(e.currentTarget);
    if (i === campos.length - 1 && ultima) {
      novaLinha();
      requestAnimationFrame(() => {
        const novos = [...(gradeRef.current?.querySelectorAll<HTMLInputElement>("input[data-celula]") ?? [])];
        novos[i + 1]?.focus();
      });
      return;
    }
    campos[i + 1]?.focus();
    campos[i + 1]?.select();
  }

  const colunas = `26px minmax(300px, 2.2fr) ${campos.map(() => "104px").join(" ")} 116px 120px 128px 118px`;

  return (
    <>
      <div className="cab-doc">
        <h1>Orçamento</h1>
        <select
          className="campo-texto foco-claro"
          style={{ width: "auto" }}
          value={parceiroId}
          onChange={(e) => setParceiroId(e.target.value)}
          aria-label="Cliente"
        >
          {clientes.map((p) => (
            <option key={p.id} value={p.id}>{p.nome}</option>
          ))}
        </select>
        {filiais.length > 1 && (
          <select
            className="campo-texto foco-claro"
            style={{ width: "auto" }}
            value={filialId}
            onChange={(e) => setFilialId(e.target.value)}
            aria-label="Filial"
          >
            {filiais.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        )}
        <span className="cod">{new Date().toLocaleDateString("pt-BR")}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="bt bt-neutro" onClick={novaLinha}>Novo item <kbd>F4</kbd></button>
          <button className="bt" onClick={() => void gravar(false)} disabled={gravando}>
            Salvar <kbd>F9</kbd>
          </button>
        </div>
      </div>

      {/* Faixa de contexto: a regra ATIVA, somente-leitura (§6.1) */}
      <div className="contexto">
        <span className="rotulo">Cubagem</span>
        <b>{rotuloDaRegra(regra)}</b>
        <span>
          · regra do contrato de {parceiro?.nome ?? "—"} · medindo {oQueMede(regra)}
        </span>
        <span className="cadeado">Definida no cadastro do parceiro — não se altera aqui</span>
      </div>

      <div className="rolagem" ref={gradeRef}>
        <div style={{ minWidth: 1180 }}>
          <div className="lin cab-grade" style={{ gridTemplateColumns: colunas, minWidth: 1180 }}>
            <div />
            <div>Produto</div>
            {campos.map((c) => <div key={c.chave} className="r">{c.rotulo}</div>)}
            <div className="r chave">Volume m³</div>
            <div className="r">Preço/m³</div>
            <div className="r">Total</div>
            <div className="r">Saldo pátio</div>
          </div>

          <div className="corpo-grade">
            {calculadas.map((c, i) => {
              const pendente = temAlgumDado(c.linha) && (!c.resultado.ok || c.semProduto);
              const disp = disponivelDaLinha(saldos[c.linha.produtoId], c.linha.medida);
              const volume = c.resultado.ok ? c.resultado.volume : null;
              const atende = volume && disp !== null ? Number(disp) >= Number(volume.paraBanco()) : null;
              return (
                <div
                  className="lin"
                  key={c.linha.chave}
                  style={{ gridTemplateColumns: colunas, minWidth: 1180 }}
                >
                  <div className="canaleta">{pendente && <span className="marca-linha" />}</div>
                  <div className="cel">
                    <span className="txt" style={{ color: c.semProduto ? "var(--texto-terciario)" : undefined }}>
                      {c.linha.produtoRotulo || "— F2 para buscar —"}
                    </span>
                  </div>
                  {campos.map((campo) => (
                    <div
                      className={`cel r${erroNoCampo(c.resultado, campo) ? " st-inval" : ""}`}
                      key={campo.chave}
                    >
                      <input
                        data-celula
                        data-linha={i}
                        inputMode="decimal"
                        value={c.linha.medida[campo.chave] ?? ""}
                        onChange={(e) => mudarCampo(i, campo.chave, e.target.value)}
                        onFocus={(e) => e.currentTarget.select()}
                        onKeyDown={(e) => aoPressionarNaCelula(e, i === calculadas.length - 1)}
                        aria-label={`${campo.rotulo} linha ${i + 1}`}
                      />
                    </div>
                  ))}
                  <div className="cel r chave num">
                    {volume ? volume.paraExibicao() : ""}
                  </div>
                  <div className="cel r">
                    <input
                      data-celula
                      data-linha={i}
                      inputMode="decimal"
                      value={c.linha.preco}
                      onChange={(e) => mudarPreco(i, e.target.value)}
                      onFocus={(e) => e.currentTarget.select()}
                      onKeyDown={(e) => aoPressionarNaCelula(e, i === calculadas.length - 1)}
                      aria-label={`Preço linha ${i + 1}`}
                    />
                  </div>
                  <div className="cel r num">
                    {c.resultado.ok ? c.resultado.total.paraExibicao() : ""}
                  </div>
                  <div className={`cel r num ${atende === null ? "" : atende ? "saldo-ok" : "saldo-falta"}`}>
                    {disp ?? ""}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lin subtotal" style={{ gridTemplateColumns: colunas, minWidth: 1180 }}>
            <div className="canaleta" />
            <div className="cel"><span className="txt">{calculadas.length} itens</span></div>
            {campos.map((c) => <div className="cel" key={c.chave} />)}
            <div className="cel r chave num">{totais.volume.paraExibicao()}</div>
            <div className="cel" />
            <div className="cel r num">{totais.valor.paraExibicao()}</div>
            <div className="cel" />
          </div>
        </div>
      </div>

      {/* Máximo 5 métricas por faixa (§4) */}
      <div className="metricas">
        <div>
          <span className="rotulo">Volume total</span>
          <span className="vl">{totais.volume.paraExibicao()} m³</span>
          <span className="ct">{calculadas.length} itens</span>
        </div>
        <div>
          <span className="rotulo">Valor total</span>
          <span className="vl">R$ {totais.valor.paraExibicao()}</span>
          <span className="ct">sem frete</span>
        </div>
        <div>
          <span className="rotulo">Peças</span>
          <span className="vl">{totais.pecas.toLocaleString("pt-BR")}</span>
          <span className="ct">total</span>
        </div>
        <div>
          <span className="rotulo">Pendências</span>
          <span className="vl" style={{ color: totais.pendencias ? "var(--erro-texto)" : undefined }}>
            {totais.pendencias}
          </span>
          <span className="ct">{totais.pendencias ? "F9 recusa" : "pronto para converter"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            className="bt"
            disabled={!podeConverter || gravando}
            onClick={() => setConfirmar(true)}
          >
            Converter em pedido <kbd>Ctrl+Enter</kbd>
          </button>
        </div>
      </div>

      {buscaAberta && (
        <BuscaProduto
          produtos={produtos}
          aoEscolher={(p) => { aplicarProduto(buscaLinha, p); setBuscaAberta(false); }}
          aoFechar={() => setBuscaAberta(false)}
        />
      )}

      {confirmar && (
        <div className="veu" onMouseDown={(e) => e.target === e.currentTarget && setConfirmar(false)}>
          <div className="dialogo" role="dialog" aria-modal="true">
            <h2>Converter em pedido?</h2>
            <p>
              São {totais.volume.paraExibicao()} m³ por R$ {totais.valor.paraExibicao()}.
              A conversão é irreversível: o orçamento fecha e o pedido passa a reservar
              esse volume no pátio.
            </p>
            <div className="acoes">
              {/* A ação primária é CANCELAR (§4) */}
              <button className="bt" autoFocus onClick={() => setConfirmar(false)}>Cancelar</button>
              <button className="bt bt-destrutivo" onClick={() => void gravar(true)} disabled={gravando}>
                Converter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- apoio */

function temAlgumDado(l: Linha): boolean {
  return l.produtoId !== "" || l.preco !== "" || Object.values(l.medida).some((v) => v !== "");
}

function erroNoCampo(
  r: ReturnType<typeof calcularLinha>,
  campo: CampoMedida,
): boolean {
  return !r.ok && r.erro.startsWith(campo.rotulo);
}

function linhaEmFoco(raiz: HTMLElement | null): number {
  const ativo = document.activeElement as HTMLElement | null;
  if (!ativo || !raiz?.contains(ativo)) return 0;
  const n = ativo.getAttribute("data-linha");
  return n === null ? 0 : Number(n);
}

function vizinhoVertical(direcao: number): HTMLInputElement | null {
  const ativo = document.activeElement as HTMLInputElement | null;
  if (!ativo?.hasAttribute("data-celula")) return null;
  const celula = ativo.closest(".cel");
  const linha = ativo.closest(".lin");
  if (!celula || !linha) return null;
  const coluna = [...linha.children].indexOf(celula);
  const outra = direcao > 0 ? linha.nextElementSibling : linha.previousElementSibling;
  if (!outra?.classList.contains("lin")) return null;
  return outra.children[coluna]?.querySelector("input") ?? null;
}

/** Casa a bitola digitada com a linha de saldo correspondente. */
function disponivelDaLinha(
  bitolas: BitolaSaldo[] | undefined,
  medida: Record<string, string>,
): string | null {
  if (!bitolas) return null;
  const esp = Number(medida["esp_mm"]);
  const larg = Number(medida["larg_mm"]);
  const comp = Number(medida["comp_m"]);
  if (!esp || !larg || !comp) return null;
  const achada = bitolas.find(
    (b) => b.espMm === esp && b.largMm === larg && Number(b.compM) === comp,
  );
  if (!achada) return "0,000";
  return Number(achada.disponivel).toLocaleString("pt-BR", {
    minimumFractionDigits: 3, maximumFractionDigits: 3,
  });
}

/* ------------------------------------------------------- overlay F2 (§4) */

function BuscaProduto({
  produtos, aoEscolher, aoFechar,
}: {
  produtos: Produto[];
  aoEscolher: (p: Produto) => void;
  aoFechar: () => void;
}) {
  const [termo, setTermo] = useState("");
  const [sel, setSel] = useState(0);

  const filtrados = useMemo(() => {
    const t = termo.trim().toLowerCase();
    const base = produtos.filter((p) => p.ativo);
    if (t === "") return base;
    return base.filter(
      (p) =>
        p.codigo.toLowerCase().includes(t) ||
        p.especie.toLowerCase().includes(t) ||
        p.forma.toLowerCase().includes(t),
    );
  }, [produtos, termo]);

  useEffect(() => { setSel(0); }, [termo]);

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); aoFechar(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, filtrados.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter") {
        e.preventDefault();
        const p = filtrados[sel];
        if (p) aoEscolher(p);
      }
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [filtrados, sel, aoEscolher, aoFechar]);

  return (
    <div className="veu" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="busca" role="dialog" aria-modal="true" aria-label="Buscar produto">
        <div className="campo">
          <span className="tag">F2</span>
          <input
            autoFocus
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Buscar por código, espécie ou forma…"
          />
        </div>
        <div className="res">
          {filtrados.length === 0 && (
            <div className="vazio"><p>Nenhum produto encontrado.</p></div>
          )}
          {filtrados.map((p, i) => (
            <div
              key={p.id}
              className={`it${i === sel ? " sel" : ""}`}
              onMouseDown={() => aoEscolher(p)}
            >
              <span className="c">{p.codigo}</span>
              <span>{p.especie}{p.classe ? ` · ${p.classe}` : ""}</span>
              <span className="c">{p.forma.toLowerCase()}</span>
              <span className="c" style={{ textAlign: "right" }}>NCM {p.ncm}</span>
            </div>
          ))}
        </div>
        <div className="rodape-busca">
          <span><kbd>↑ ↓</kbd> navegar</span>
          <span><kbd>Enter</kbd> aplicar na linha</span>
          <span><kbd>Esc</kbd> fechar</span>
        </div>
      </div>
    </div>
  );
}
